import { gql, useQuery } from '@apollo/client';
import React from 'react';
import { Redirect } from 'react-router-dom';

import { org } from 'src/app/account/routes';
import { Loading } from 'src/components/common/loading/Loading';
import { NotFound } from 'src/components/common/notFound/NotFound';
import { useCapabilities } from 'src/hooks/useCapabilities';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { useShowSelfServeScaleUI } from 'src/lib/featureFlags';
import {
  AccountLockType,
  BillingPlanKind,
  PlanProtectedAreaQuery,
  PlanProtectedAreaQueryVariables,
} from 'src/lib/graphqlTypes/types';

import { PermissionGuard } from '../permissionGuards/permissionGuard/PermissionGuard';

import { FeatureUnavailable } from './featureUnavailable/FeatureUnavailable';
const ExpiredTeamTrial = React.lazy(async () => ({
  default: (
    await import(
      /* webpackPrefetch: true, webpackChunkName: "ExpiredTeamTrial" */ './expiredTrials/expiredTeamTrial/ExpiredTeamTrial'
    )
  ).ExpiredTeamTrial,
}));
const ExpiredEnterpriseTrial = React.lazy(async () => ({
  default: (
    await import(
      /* webpackPrefetch: true, webpackChunkName: "ExpiredEnterpriseTrial" */ './expiredTrials/expiredEnterpriseTrial/ExpiredEnterpriseTrial'
    )
  ).ExpiredEnterpriseTrial,
}));
const LockedOrg = React.lazy(async () => ({
  default: (
    await import(
      /* webpackPrefetch: true, webpackChunkName: "LockedOrg" */ './lockedOrg/LockedOrg'
    )
  ).LockedOrg,
}));
const RestorableOrg = React.lazy(async () => ({
  default: (
    await import(
      /* webpackPrefetch: true, webpackChunkName: "RestorableOrg" */ './restorableOrg/RestorableOrg'
    )
  ).RestorableOrg,
}));

interface Props {
  accountId: string | null;
  requiresPlanCapability?: Exclude<
    keyof NonNullable<ReturnType<typeof useCapabilities>['capabilities']>,
    '__typename'
  >;
  children: React.ReactNode;
  redirectExpiredToOrg?: boolean;
}

export const planProtectedAreaQuery = gql<
  PlanProtectedAreaQuery,
  PlanProtectedAreaQueryVariables
>`
  query PlanProtectedAreaQuery($accountId: ID!) {
    account(id: $accountId) {
      expiredTrialSubscription {
        uuid
        plan {
          id
          kind
        }
      }
      id
      name
      isOnExpiredTrial
      lockType
      isLocked
      currentPlan {
        id
        kind
        tier
      }
    }
  }
`;

export const PlanProtectedArea = ({
  accountId = '',
  requiresPlanCapability,
  children,
  redirectExpiredToOrg = false,
}: Props) => {
  const showScaleUI = useShowSelfServeScaleUI();
  const { data, loading, error } = useQuery(planProtectedAreaQuery, {
    variables: {
      accountId: accountId as string, // safe because of the skip below
    },
    skip: !accountId,
    context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
  });
  const {
    capabilities,
    error: capabilitiesError,
    loading: capabilitiesLoading,
  } = useCapabilities(!requiresPlanCapability);

  if (error || capabilitiesError) {
    console.warn(error || capabilitiesError); // eslint-disable-line no-console
    return <NotFound />;
  }
  if (loading || capabilitiesLoading) return <Loading />;

  const isLocked = data?.account?.isLocked;
  const lockType = data?.account?.lockType;
  const isOnExpiredTrial = !!data?.account?.isOnExpiredTrial;
  const isOnExpiredTeamTrial =
    isOnExpiredTrial &&
    data?.account?.expiredTrialSubscription?.plan?.kind ===
      BillingPlanKind.TEAM_TRIAL;
  const isOnExpiredEnterpriseTrial =
    isOnExpiredTrial &&
    data?.account?.expiredTrialSubscription?.plan?.kind ===
      BillingPlanKind.ENTERPRISE_TRIAL;

  const orgId = accountId || '';
  const orgName = data?.account?.name || '';

  // Show LockedOrg for locked accounts unless they have were locked by the org admin
  const shouldShowLockedPage = isLocked;

  // Show RestorableOrg for locked accounts with lockType MANUAL_BY_ORG_ADMIN
  const shouldShowRestorablePage =
    showScaleUI && isLocked && lockType === AccountLockType.MANUAL_BY_ORG_ADMIN;

  return (
    <PermissionGuard
      accountPermissions="canSetConstrainedPlan"
      accountId={accountId ?? undefined}
      fallback={
        <React.Suspense fallback={<Loading />}>
          {isOnExpiredEnterpriseTrial ? (
            <ExpiredEnterpriseTrial accountId={orgId} />
          ) : shouldShowRestorablePage ? (
            <RestorableOrg accountId={orgId} />
          ) : shouldShowLockedPage ? (
            <LockedOrg accountId={orgId} orgName={orgName} />
          ) : requiresPlanCapability &&
            !capabilities?.[requiresPlanCapability] ? (
            <FeatureUnavailable />
          ) : (
            <>{children}</>
          )}
        </React.Suspense>
      }
      childRenderer={(hasPermission) => {
        return (
          <React.Suspense fallback={<Loading />}>
            {isOnExpiredEnterpriseTrial ? (
              <ExpiredEnterpriseTrial accountId={orgId} />
            ) : shouldShowRestorablePage ? (
              <RestorableOrg accountId={orgId} />
            ) : /**
             * Show LockedOrg for locked accounts unless they have a SCALE plan
             */
            shouldShowLockedPage ? (
              <LockedOrg accountId={orgId} orgName={orgName} />
            ) : /**
             * Always protect the route for expired trials, regardless of if the plan
             * allows the feature if the user has permission to set the plan.
             */
            isOnExpiredTeamTrial && hasPermission ? (
              redirectExpiredToOrg ? (
                <Redirect to={org.location({ orgId })} />
              ) : (
                <ExpiredTeamTrial accountId={orgId} />
              )
            ) : /**
             * If there is a plan flag required to access this page but this flag is not
             * enabled for this plan, display upgrade UX.
             */
            requiresPlanCapability &&
              !capabilities?.[requiresPlanCapability] ? (
              <FeatureUnavailable />
            ) : (
              <>{children}</>
            )}
          </React.Suspense>
        );
      }}
    />
  );
};
